import { computed, ref, watch, type ComputedRef } from 'vue';

export type NavigateMods = { ctrlKey: boolean; shiftKey: boolean };

export function useTableSelection<T extends Record<string, unknown>>(params: {
  data: ComputedRef<T[]>;
  rowIdKey: (value: T) => string;
  allowRowSelection: ComputedRef<boolean>;
  onSelectionChange: (selectedIds: Set<string>, selectedData: T[]) => void;
  isPlaceholderRow?: (row: T) => boolean;
  isRowSelectable?: (row: T) => boolean;
}) {
  const selectedIds = ref<Set<string>>(new Set());
  const anchorIndex = ref<number | null>(null);

  function isRowSelectable(row: T | undefined): row is T {
    return !!row && !params.isPlaceholderRow?.(row) && params.isRowSelectable?.(row) !== false;
  }
  const selectableIds = computed(
    () => new Set(params.data.value.filter(isRowSelectable).map(params.rowIdKey))
  );

  const selectedData = computed(() =>
    params.data.value.filter((row) => selectedIds.value.has(params.rowIdKey(row)))
  );

  function isSelected(rowId: string): boolean {
    return selectedIds.value.has(rowId);
  }

  function applyModifiers(targetIdx: number, mods: NavigateMods) {
    if (!params.allowRowSelection.value) return;
    const row = params.data.value[targetIdx];
    if (!isRowSelectable(row)) return;
    const id = params.rowIdKey(row);

    if (!mods.ctrlKey && !mods.shiftKey) {
      selectedIds.value = new Set([id]);
      anchorIndex.value = targetIdx;
    } else if (mods.ctrlKey && !mods.shiftKey) {
      const next = new Set(selectedIds.value);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      selectedIds.value = next;
      anchorIndex.value = targetIdx;
    } else if (mods.shiftKey && !mods.ctrlKey) {
      selectedIds.value = new Set(rangeIds(targetIdx));
    } else {
      const next = new Set(selectedIds.value);
      for (const rangeId of rangeIds(targetIdx)) next.add(rangeId);
      selectedIds.value = next;
    }
  }

  /** Row ids from the shift anchor to `targetIdx`. A stale anchor (data
   *  shrank between clicks — refetch, pagination) is clamped into the data
   *  and rows missing from a sparse window are skipped: a shift-click must
   *  never throw on `data[i]` being undefined, that swallowed the whole
   *  click (I-180 "cannot select two positions"). An unset anchor is
   *  established at the clicked row, so shift-click-first behaves like a
   *  plain click and extends from there on the next shift-click. */
  function rangeIds(targetIdx: number): string[] {
    if (anchorIndex.value === null) anchorIndex.value = targetIdx;
    return idsBetween(anchorIndex.value, targetIdx);
  }

  /** Ids of the loaded, non-placeholder rows between both indices (either order, clamped). */
  function idsBetween(aIdx: number, bIdx: number): string[] {
    const lastIdx = params.data.value.length - 1;
    const from = Math.max(0, Math.min(aIdx, bIdx));
    const to = Math.min(lastIdx, Math.max(aIdx, bIdx));
    const ids: string[] = [];
    for (let i = from; i <= to; i++) {
      const row = params.data.value[i];
      if (isRowSelectable(row)) ids.push(params.rowIdKey(row));
    }
    return ids;
  }

  function toggleRowSelection(rowIdx: number, event: { ctrlKey: boolean; shiftKey: boolean }) {
    if (!params.allowRowSelection.value) return;
    applyModifiers(rowIdx, { ctrlKey: event.ctrlKey, shiftKey: event.shiftKey });
  }

  /**
   * Checkbox semantics of a Shift+click: every row from the anchor to
   * `targetIdx` gets `selected` — the clicked box's new state — while marks
   * outside the range stay. The clicked row anchors the next range.
   */
  function setRangeSelected(targetIdx: number, selected: boolean) {
    if (!params.allowRowSelection.value) return;
    const row = params.data.value[targetIdx];
    if (!isRowSelectable(row)) return;
    const next = new Set(selectedIds.value);
    for (const id of rangeIds(targetIdx)) {
      if (selected) next.add(id);
      else next.delete(id);
    }
    selectedIds.value = next;
    anchorIndex.value = targetIdx;
  }

  /**
   * Drag selection: the rows from `fromIdx` to `toIdx` (either order) become
   * the selection, on top of `base` when given. `fromIdx` — where the drag
   * started — anchors a later Shift+click or Shift+arrow. Called on every row
   * the pointer passes, so an unchanged set keeps its identity.
   */
  function selectRange(fromIdx: number, toIdx: number, base?: ReadonlySet<string>) {
    if (!params.allowRowSelection.value) return;
    const next = new Set([...(base ?? [])].filter((id) => selectableIds.value.has(id)));
    for (const id of idsBetween(fromIdx, toIdx)) next.add(id);
    anchorIndex.value = fromIdx;
    const unchanged =
      next.size === selectedIds.value.size && [...next].every((id) => selectedIds.value.has(id));
    if (!unchanged) selectedIds.value = next;
  }

  function selectRow(rowId: string) {
    if (!params.allowRowSelection.value) return;
    const idx = params.data.value.findIndex((v) => params.rowIdKey(v) === rowId);
    if (!isRowSelectable(params.data.value[idx])) return;
    selectedIds.value = new Set([rowId]);
    anchorIndex.value = idx;
  }

  function selectAll() {
    if (!params.allowRowSelection.value) return;
    selectedIds.value = new Set(selectableIds.value);
  }

  function deselectAll() {
    if (!params.allowRowSelection.value) return;
    selectedIds.value = new Set();
    anchorIndex.value = null;
  }

  function setSelectedIds(ids: Iterable<string>) {
    if (!params.allowRowSelection.value) return;
    const next = new Set([...ids].filter((id) => selectableIds.value.has(id)));
    const unchanged =
      next.size === selectedIds.value.size && [...next].every((id) => selectedIds.value.has(id));
    if (unchanged) return;
    selectedIds.value = next;
    anchorIndex.value =
      next.size > 0 ? params.data.value.findIndex((row) => next.has(params.rowIdKey(row))) : null;
  }

  watch(
    () => params.data.value,
    (newData, oldData) => {
      if (anchorIndex.value !== null && oldData) {
        const prevAnchor = oldData[anchorIndex.value];
        if (prevAnchor) {
          const id = params.rowIdKey(prevAnchor);
          const newIdx = newData.findIndex((v) => params.rowIdKey(v) === id);
          anchorIndex.value = newIdx === -1 ? null : newIdx;
        } else {
          anchorIndex.value = null;
        }
      }
    },
    { flush: 'post' }
  );

  watch(
    selectableIds,
    (availableIds) => {
      // Prune ONLY when a selected row disappeared or became ineligible. Unconditionally
      // assigning a fresh Set on every data swap changes the ref's identity,
      // dirties selectedData, and any consumer rendering selection state
      // (e.g. the positions grouping bar) re-renders → with per-render prop
      // literals in the parent that update loop escalates into Vue's
      // "Maximum recursive updates" and kills the grid (I-180 add-row crash).
      const pruned = [...selectedIds.value].filter((id) => availableIds.has(id));
      if (pruned.length !== selectedIds.value.size) {
        selectedIds.value = new Set(pruned);
      }
    },
    { flush: 'post' }
  );

  watch(selectedIds, () => {
    params.onSelectionChange(selectedIds.value, selectedData.value);
  });

  return {
    selectedIds,
    selectedData,
    anchorIndex,
    isRowSelectable,
    selectableIds,
    isSelected,
    toggleRowSelection,
    setRangeSelected,
    selectRange,
    selectRow,
    selectAll,
    deselectAll,
    setSelectedIds,
  };
}

export type TableSelection<T extends Record<string, unknown>> = ReturnType<
  typeof useTableSelection<T>
>;
